import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { learning } from "@/api";
import { PILLARS, TIER_META } from "@/constants";
import {
  GraduationCap, HelpCircle, PlayCircle, Image as ImageIcon, FileText,
  ExternalLink, Sparkles, Filter, X, Check, Pin, BookOpen,
} from "lucide-react";
import Modal from "@/components/Modal";

const TYPE_META = {
  video:       { label: "Video",       icon: PlayCircle, color: "#E4002B" },
  infographic: { label: "Infografía",  icon: ImageIcon,  color: "#D4A437" },
  document:    { label: "Documento",   icon: FileText,   color: "#0033AD" },
  link:        { label: "Enlace",      icon: ExternalLink, color: "#009639" },
  capsule:     { label: "Cápsula",     icon: Sparkles,   color: "#3F4566" },
  article:     { label: "Artículo",    icon: BookOpen,   color: "#0033AD" },
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const absMedia = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

// --- Simple, safe Markdown → HTML (very small subset) ------------------------
function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[c]);
}

function renderMarkdown(md) {
  if (!md) return "";
  const lines = String(md).split(/\r?\n/);
  const out = [];
  let inUl = false;
  const flushUl = () => { if (inUl) { out.push("</ul>"); inUl = false; } };
  const inline = (t) => escapeHtml(t)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, '<code class="bg-[var(--cream-deep)] px-1 rounded">$1</code>')
    .replace(/\[([^\]]+)\]\((https?:[^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[var(--lala-blue)] underline">$1</a>');
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushUl(); out.push(""); continue; }
    if (line.startsWith("# ")) { flushUl(); out.push(`<h1 class="font-display text-3xl mt-4 mb-2">${inline(line.slice(2))}</h1>`); continue; }
    if (line.startsWith("## ")) { flushUl(); out.push(`<h2 class="font-display text-2xl mt-4 mb-2">${inline(line.slice(3))}</h2>`); continue; }
    if (line.startsWith("### ")) { flushUl(); out.push(`<h3 class="font-display text-xl mt-3 mb-1">${inline(line.slice(4))}</h3>`); continue; }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inUl) { out.push('<ul class="list-disc pl-6 my-2 space-y-1">'); inUl = true; }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    flushUl();
    out.push(`<p class="my-2 leading-relaxed">${inline(line)}</p>`);
  }
  flushUl();
  return out.join("\n");
}

// --- Embed detection ---------------------------------------------------------
function classifyUrl(url) {
  if (!url) return { kind: "none" };
  const u = url.toString();
  const isOwn = u.startsWith("/api/media/");
  const full = absMedia(u);
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return { kind: "youtube", embed: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "vimeo", embed: `https://player.vimeo.com/video/${vm[1]}` };
  const ext = (u.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();
  if (["mp4", "webm", "mov"].includes(ext)) return { kind: "video-file", src: full };
  if (["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(ext)) return { kind: "image", src: full };
  if (ext === "pdf") return { kind: "pdf", src: full };
  if (["mp3", "wav"].includes(ext)) return { kind: "audio", src: full };
  return { kind: "external", href: u, isOwn };
}

export default function Learning() {
  const [items, setItems] = useState([]);
  const [resources, setResources] = useState([]);
  const [pillar, setPillar] = useState("");
  const [tier, setTier] = useState("");
  const [type, setType] = useState("");
  const [openRes, setOpenRes] = useState(null);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    learning.list({ ...(pillar && { pillar }) }).then(setItems);
  }, [pillar]);

  useEffect(() => {
    learning.resources.list({
      ...(pillar && { pillar }),
      ...(tier && { tier_level: tier }),
      ...(type && { type }),
    }).then(setResources);
  }, [pillar, tier, type]);

  useEffect(() => {
    learning.progress.list().then((rows) => {
      const m = {};
      for (const r of rows) if (r.resource_id) m[r.resource_id] = r;
      setProgress(m);
    }).catch(() => {});
  }, []);

  const capsules = useMemo(() => items.filter((i) => i.type === "capsule"), [items]);
  const faqs = useMemo(() => items.filter((i) => i.type === "faq"), [items]);

  const markCompleted = async (r) => {
    try {
      await learning.progress.upsert({ resource_id: r.id, completed: true });
      setProgress((p) => ({ ...p, [r.id]: { resource_id: r.id, completed: true } }));
    } catch (_) {}
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="learning-page">
      <div>
        <div className="font-hand text-xl text-[var(--lala-blue)]">centro de aprendizaje</div>
        <h1 className="font-display text-4xl md:text-5xl">Entiende el programa. Sube de nivel.</h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">
          Contenido oficial de Lala para que entiendas qué significa cada pilar, cómo se calcula tu score y qué espera Lala de sus proveedores.
        </p>
      </div>

      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="learning-explainers">
        <Explainer icon={<HelpCircle size={20} />} title="Qué significa cada pilar"
          body="Conoce los 6 pilares (Calidad, Costo, Nivel de Servicio, Innovación, Sostenibilidad y Cumplimiento) y qué pesa cada uno." />
        <Explainer icon={<GraduationCap size={20} />} title="Qué significa cada nivel"
          body="Platino, Oro, Plata, Bronce y Recuperación. Cada metal tiene un rango de score y compromisos asociados." />
        <Explainer icon={<Sparkles size={20} />} title="Cómo se calcula tu score"
          body="Tu score global es el promedio ponderado de los 6 pilares. Lala alimenta los datos, valida y publica el período." />
        <Explainer icon={<FileText size={20} />} title="Cómo interpretar tu reporte"
          body="Tu reporte mensual muestra score, pilares, evolución y recomendaciones. Es informativo: no requiere acción tuya en plataforma." />
        <Explainer icon={<PlayCircle size={20} />} title="Qué espera Lala de ti"
          body="Mantener tus compromisos comerciales, documentación al día con tu comprador, comunicación abierta y mejora continua." />
        <Explainer icon={<ImageIcon size={20} />} title="Cómo avanzar al siguiente nivel"
          body="Sube consistentemente tus pilares más bajos. Tu comprador Lala es tu canal de mejora; aquí solo consultas tu desempeño." />
      </section>

      <div className="flex flex-wrap gap-2 mt-6 sketch-border bg-[var(--cream-soft)] p-3" data-testid="learning-filters">
        <div className="flex items-center gap-2 text-[var(--ink-soft)] text-xs uppercase tracking-wider font-semibold pr-2 border-r border-[var(--line)]">
          <Filter size={14} /> Filtros
        </div>
        <select value={pillar} onChange={(e) => setPillar(e.target.value)}
          className="input-field max-w-[200px]" data-testid="learning-pillar">
          <option value="">Todos los pilares</option>
          {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select value={tier} onChange={(e) => setTier(e.target.value)}
          className="input-field max-w-[200px]" data-testid="learning-tier">
          <option value="">Todos los niveles</option>
          {Object.entries(TIER_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="input-field max-w-[200px]" data-testid="learning-type">
          <option value="">Todos los formatos</option>
          {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>

      <section className="mt-8" data-testid="resources-section">
        <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
          <GraduationCap size={18} /> recursos curados
        </div>
        {resources.length === 0 ? (
          <div className="text-center text-[var(--ink-soft)] py-10 sketch-border bg-[var(--cream-soft)] mt-3" data-testid="resources-empty">
            Sin recursos para esos filtros. Prueba con otra combinación.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-3">
            {resources.map((r) => (
              <ResourceCard key={r.id} r={r}
                completed={!!progress[r.id]?.completed}
                onOpen={() => setOpenRes(r)} />
            ))}
          </div>
        )}
      </section>

      {/* Legacy capsules */}
      {capsules.length > 0 && (
        <section className="mt-12">
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Sparkles size={18} /> cápsulas escritas
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-3">
            {capsules.map((c) => (
              <article key={c.id} className="sketch-border bg-[var(--cream-soft)] p-5" data-testid={`capsule-${c.id}`}>
                {c.pillar && <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">{PILLARS.find((p) => p.key === c.pillar)?.label || c.pillar}</div>}
                <h3 className="font-display text-xl mt-1">{c.title}</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{c.content}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="mt-12 mb-10">
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <HelpCircle size={18} /> preguntas frecuentes
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-3">
            {faqs.map((c) => (
              <article key={c.id} className="sketch-border bg-[var(--cream-soft)] p-5" data-testid={`faq-${c.id}`}>
                <h3 className="font-display text-lg">{c.title}</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{c.content}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {openRes && (
        <ResourceViewer resource={openRes}
          onClose={() => setOpenRes(null)}
          onMarkCompleted={() => markCompleted(openRes)} />
      )}
    </div>
  );
}

function ResourceCard({ r, completed, onOpen }) {
  const t = TYPE_META[r.type] || TYPE_META.capsule;
  const Icon = t.icon;
  const pillarLabel = PILLARS.find((p) => p.key === r.pillar)?.label;
  const tierMeta = r.tier_level && r.tier_level !== "all" ? TIER_META[r.tier_level] : null;
  return (
    <button
      onClick={onOpen}
      className="sketch-border bg-[var(--cream-soft)] hover:-translate-y-0.5 transition-transform text-left overflow-hidden group relative"
      data-testid={`resource-${r.id}`}
      style={{ borderColor: t.color }}
    >
      {r.cover_url && (
        <div className="w-full aspect-video bg-[var(--cream-deep)] overflow-hidden">
          <img src={absMedia(r.cover_url)} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2">
          <Icon size={20} style={{ color: t.color }} />
          <span className="text-xs uppercase tracking-wider font-bold" style={{ color: t.color }}>{t.label}</span>
          {r.is_pinned && <Pin size={12} className="text-[var(--lala-blue)] ml-1" />}
          {r.duration_minutes && (
            <span className="text-[10px] text-[var(--ink-soft)] ml-auto">{r.duration_minutes} min</span>
          )}
          {completed && (
            <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5" data-testid={`resource-completed-${r.id}`}>
              <Check size={12} /> visto
            </span>
          )}
        </div>
        <h3 className="font-display text-xl mt-2 leading-tight">{r.title}</h3>
        {r.description && (
          <p className="text-sm text-[var(--ink-soft)] mt-2 line-clamp-3">{r.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {pillarLabel && (
            <span className="text-[10px] uppercase tracking-wider font-semibold bg-[var(--cream-deep)] text-[var(--ink)] px-2 py-0.5 rounded-full">
              {pillarLabel}
            </span>
          )}
          {tierMeta && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
              style={{ background: tierMeta.color + "22", color: tierMeta.color, border: `1px solid ${tierMeta.color}` }}>
              Nivel {tierMeta.label}
            </span>
          )}
          {(!r.tier_level || r.tier_level === "all") && (
            <span className="text-[10px] uppercase tracking-wider font-semibold bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] px-2 py-0.5 rounded-full">
              Todos los niveles
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ResourceViewer({ resource, onClose, onMarkCompleted }) {
  const info = classifyUrl(resource.url);
  const hasBody = !!resource.body_markdown;
  useEffect(() => {
    // auto-mark on open (best-effort)
    if (onMarkCompleted) onMarkCompleted();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal testId="resource-viewer" align="center" backdrop="bg-black/60" onClose={onClose} closeOnOverlay>
      <div className="bg-[var(--cream)] w-full max-w-4xl max-h-[92vh] rounded-2xl border border-[var(--line)] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[var(--line)] bg-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">{TYPE_META[resource.type]?.label || resource.type}</div>
            <h2 className="font-display text-xl truncate">{resource.title}</h2>
          </div>
          <button onClick={onClose} className="text-[var(--ink-soft)] hover:text-[var(--ink)] p-1" data-testid="viewer-close">
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {info.kind === "youtube" || info.kind === "vimeo" ? (
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden" data-testid="viewer-embed">
              <iframe src={info.embed} title={resource.title}
                className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
            </div>
          ) : info.kind === "video-file" ? (
            <video controls src={info.src} className="w-full max-h-[70vh] rounded-lg bg-black" data-testid="viewer-video" />
          ) : info.kind === "image" ? (
            <img src={info.src} alt={resource.title} className="w-full max-h-[70vh] object-contain rounded-lg" data-testid="viewer-image" />
          ) : info.kind === "pdf" ? (
            <iframe src={info.src} className="w-full h-[70vh] rounded-lg border border-[var(--line)]" title={resource.title} data-testid="viewer-pdf" />
          ) : info.kind === "audio" ? (
            <audio controls src={info.src} className="w-full" data-testid="viewer-audio" />
          ) : info.kind === "external" ? (
            <div className="text-center py-8 text-[var(--ink-soft)]" data-testid="viewer-external">
              Este recurso se abre en una pestaña externa.
              <div className="mt-3">
                <a href={info.href} target="_blank" rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2 text-sm">
                  <ExternalLink size={14} /> Abrir enlace
                </a>
              </div>
            </div>
          ) : null}

          {hasBody && (
            <article className="prose prose-sm max-w-none mt-6 text-[var(--ink)]"
              data-testid="viewer-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(resource.body_markdown)) }} />
          )}

          {resource.description && !hasBody && (
            <p className="text-sm text-[var(--ink-soft)] mt-4 leading-relaxed">{resource.description}</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Explainer({ icon, title, body }) {
  return (
    <div className="sketch-border-soft bg-white p-4 flex items-start gap-3" data-testid={`explainer-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="shrink-0 w-9 h-9 rounded-full bg-[var(--lala-blue)] text-white flex items-center justify-center">{icon}</div>
      <div>
        <div className="font-display text-base text-[var(--ink)]">{title}</div>
        <p className="text-sm text-[var(--ink-soft)] mt-0.5 leading-snug">{body}</p>
      </div>
    </div>
  );
}
